<template>
  <div v-if="selected.length" class="selected-terms">
    <div class="flex flex-wrap items-center justify-start gap-1.5">
      <NuxtLink
        v-for="(item, i) in selected"
        :key="i"
        class="button is-small"
        :to="item.to"
      >
        <span :class="item.class">{{ item.label }}</span>
        <SpriteSymbol name="close" />
      </NuxtLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { LocationFilter, SearchFacet } from '~/layers/legacy-search/types'
import { DomainId } from '#graphql-operations'
import { getQueryParamString } from '~/helpers'
import {
  filterRouteQuery,
  getFilterCategoryColor,
  getTermLink,
} from '~/layers/legacy-search/helpers/search'

const { getFacetLabel } = await useSearchData()

interface SelectedTerm {
  label: string
  to: any // @TODO: Could be typed more specifically
  class?: string
}

const props = withDefaults(
  defineProps<{
    facets: SearchFacet[]
    locationFilters: LocationFilter[]
  }>(),
  {
    facets: () => [],
    locationFilters: () => [],
  },
)

const route = useRoute()
const { $texts } = useEasyTexts()

// @TODO original:
/*
activeTerms(): SelectedTerm[] {
 return this.facets
   .filter((v) => v.key !== 'filter_category')
   .map((f) =>
     f.terms
       .filter((t) => {
         const activeDomain = this.$store.state.activeDomain
         if (
           activeDomain === Domains.HoteljobsZuerichCh &&
           f.key === 'accommodation_type'
         ) {
           return false
         }
         return t.active
       })
       .map((t) => {
         return {
           label: t.label,
           class: '',
           to: {
             name: this.$route.name || '',
             ...getTermLink(this.$route.query, f.key, t),
           },
         }
       })
   )
   .flat()
}
*/

const { activeDomain } = useSiteContext()

const activeTerms = computed(() => {
  return props.facets
    .filter((v) => v.key !== 'filter_category')
    .map((f) =>
      f.terms
        .filter((t) => {
          if (
            activeDomain === DomainId.hoteljobs_zuerich_ch &&
            f.key === 'accommodation_type'
          ) {
            return false
          }
          return t.active
        })
        .map((t) => {
          return {
            label: t.label,
            class: '',
            to: {
              name: route.name || '',
              ...getTermLink(route.query, f.key, t),
            },
          }
        }),
    )
    .flat()
})

// @TODO original:
/*
activeFilters(): SelectedTerm[] {
 const text = getQueryParamString(this.$route.query, 'text')

 if (text) {
   const query = { ...this.$route.query }
   delete query.text
   return [
     {
       label: text,
       class: '',
       to: {
         name: this.$route.name || '',
         query: filterRouteQuery(query),
       },
     },
   ]
 }

 return []
}
*/
const activeFilters = computed(() => {
  const text = getQueryParamString(route.query, 'text')

  if (text) {
    const query = { ...route.query }
    delete query.text
    return [
      {
        label: text,
        class: '',
        to: {
          name: route.name || '',
          query: filterRouteQuery(query),
        },
      },
    ]
  }

  return []
})

const filterPageCategory = computed(() => {
  const category = (route.query.category as string) || ''

  if (category) {
    const query = { ...route.query }
    delete query.category
    return {
      label: getFacetLabel(category) || category,
      to: {
        name: route.name || '',
        query: filterRouteQuery(query),
      },
      class: 'text-' + getFilterCategoryColor(category),
    }
  }

  return null
})

// @TODO original:
/*
filterLocation(): SelectedTerm | null {
 const locationId = (this.$route.query.location as string) || ''
 const location = this.locationFilters.find((v) => v.id === locationId)

 if (location) {
   const query = { ...this.$route.query }
   delete query.location
   return {
     label: location.title,
     to: {
       name: this.$route.name || '',
       query: filterRouteQuery(query),
     },
     class: '',
   }
 }

 return null
}
*/
const filterLocation = computed(() => {
  const locationId = (route.query.location as string) || ''
  const location = props.locationFilters.find((v) => v.id === locationId)

  if (location) {
    const query = { ...route.query }
    delete query.location
    return {
      label: location.title,
      to: {
        name: route.name || '',
        query: filterRouteQuery(query),
      },
      class: '',
    }
  }

  return null
})

// @TODO original:
/*
currentlyOpen(): SelectedTerm | null {
 const isActive = getQueryParamString(this.$route.query, 'open') === 'true'

 if (isActive) {
   const query = { ...this.$route.query }
   delete query.open
   return {
     label: this.$store.state.search.translations.currentlyOpen,
     class: '',
     to: {
       name: this.$route.name || '',
       query: filterRouteQuery(query),
     },
   }
 }

 return null
}
*/
const currentlyOpen = computed(() => {
  const isActive = getQueryParamString(route.query, 'open') === 'true'

  if (isActive) {
    const query = { ...route.query }
    delete query.open
    return {
      label: $texts('currentlyOpen', 'Currently open'),
      class: '',
      to: {
        name: route.name || '',
        query: filterRouteQuery(query),
      },
    }
  }

  return null
})

// @TODO original:
/*
selected() {
 const selected: SelectedTerm[] = [
   ...this.activeTerms,
   ...this.activeFilters,
 ]

 if (this.filterLocation) {
   selected.push(this.filterLocation)
 }

 if (this.filterPageCategory) {
   selected.unshift(this.filterPageCategory)
 }

 if (this.currentlyOpen) {
   selected.unshift(this.currentlyOpen)
 }

 return selected.map((v) => {
   if (v.to.query) {
     delete v.to.query.page
   }
   return v
 })
}
*/
const selected = computed(() => {
  const selectedTerms: SelectedTerm[] = [
    ...activeTerms.value,
    ...activeFilters.value,
  ]

  if (filterLocation.value) {
    selectedTerms.push(filterLocation.value)
  }

  if (filterPageCategory.value) {
    selectedTerms.unshift(filterPageCategory.value)
  }

  if (currentlyOpen.value) {
    selectedTerms.unshift(currentlyOpen.value)
  }

  return selectedTerms.map((v) => {
    if (v.to.query) {
      delete v.to.query.page
    }
    return v
  })
})
</script>

<style lang="postcss">
.selected-terms {
  @apply pb-size-5;

  & a.button.is-small {
    padding: 4.5px 13px;
    height: 39px;
  }
}
</style>
